




















































































































import {
  reactive, computed, defineComponent,
  nextTick, ComputedRef, ref, PropType, watch, watchEffect
} from '@vue/composition-api'
import debounce from 'lodash/debounce'
import * as utils from '@horizon/common/utils'
import {Param, IntParamDef} from '@/params'
import NumberInput from '@/components/NumberInput.vue'
import AccountCredits from '@/components/Account/AccountCredits.vue'
import type {RenderQuality} from '@horizon/common/models'
import StripeCard from '@/components/StripeCard.vue'
import {account} from '@/watch-account'
import {renderResolutionsByName, RenderResolution} from '@/resolutions'
import type {Breakpoint} from 'vuetify/types/services/breakpoint'
import {getRenderPrice} from '@/render-cost'
import type {DoRenderArgs} from '@/components/NGL/SceneExportMixin'
import {trackEvent, trackBeginCheckout} from '@/analytics'

const components = {
  NumberInput,
  AccountCredits,
  StripeCard
}

export const renderDialogProps = {
  renderResolutions: {
    type: Array,
    default: () => [],
  },
  defaultRenderResolution: {
    type: String,
    default: 'Full HD (1080p)' as RenderResolution,
  },
  defaultRenderResolutionStill: {
    type: String,
    default: 'Standard HD (720p)' as RenderResolution,
  },
  nFrames: {
    type: Number,
    default: 1,
  },
  mode: {
    type: String as PropType<'video'|'still'>,
    default: 'video'
  },
  renderQuality: {
    type: String as PropType<RenderQuality>,
    default: 'final'
  },
  frame: {                      // only used when mode=still
    type: Number,
    default: 1,
  },
}

function createParams(props: {nFrames: number}) {
  const frameNumberParamDef = new IntParamDef({
    step: 1,
    min: 1,
    max: props.nFrames,
  })
  return {
    frameNumberParamDef,
    startParam: new Param('start', frameNumberParamDef, 1),
    endParam: new Param('end', frameNumberParamDef, props.nFrames),
  }
}

export default defineComponent({
  name: 'RenderDialog',
  components,
  props: renderDialogProps,
  setup(props, context) {
    const state = reactive({
      dialogOpen: false,
      paymentDialogOpen: false,
      frameStart: props.mode === 'still' ? props.frame : 1,
      frameEnd: props.mode === 'still' ? props.frame : props.nFrames,
      renderResolution: props.mode === 'still' ? props.defaultRenderResolutionStill : props.defaultRenderResolution,
      renderQuality: props.renderQuality as RenderQuality,
      renderResolutions: props.renderResolutions,
      cost: 0,
      costLoading: false,
      costError: [] as string[],
      ...createParams(props),
      renderDisabled: true,
    })
    const accountCredits: any = ref(null)

    const close = () => {
      state.dialogOpen = false
    }

    const setFrameStart = (value: number) => {
      state.frameStart = utils.clamp(value, 1, props.nFrames)
      if (state.frameEnd < state.frameStart)
        state.frameEnd = state.frameStart
      if (props.mode === 'still')
        state.frameEnd = state.frameStart // always the same in still mode
    }
    const setFrameEnd = (value: number) => {
      state.frameEnd = utils.clamp(value, 1, props.nFrames)
      if (state.frameStart > state.frameEnd)
        state.frameStart = state.frameEnd
    }

    // update param range when nFrames (shot duration) changes
    watch(() => props.nFrames,
          (nFrames) => {
            state.frameNumberParamDef.max = nFrames
            state.frameNumberParamDef.sliderMax = nFrames
            if (state.frameStart > nFrames)
              state.frameStart = nFrames
            if (props.mode != 'still' || state.frameEnd > nFrames)
              state.frameEnd = nFrames
          })
    const render = () => {
      const doRenderArgs: DoRenderArgs = {resolution: state.renderResolution as RenderResolution,
                                          startFrame: state.frameStart, endFrame: state.frameEnd,
                                          quality: state.renderQuality}
      context.emit('render', doRenderArgs)
      trackEvent('app_render', {
        resolution: state.renderResolution,
        quality: state.renderQuality,
        mode: props.mode,
        'n_frames': state.frameEnd - state.frameStart + 1,
        cost: state.cost
      })
      state.dialogOpen = false
    }

    async function setCost(nFrames: number, width: number, height: number,
                           type: 'video'|'still', quality: RenderQuality) {
      state.costError = []
      try {
        state.cost = await getRenderPrice(nFrames, width, height, type, quality)
      } catch (err) {
        state.costError.push(err.message)
      }
      state.costLoading = false
    }

    const extraCreditsNeeded = computed(() => {
      return state.cost - (account.value?.creditsAvailable || 0)
    })

    const setCostDebounced = debounce(setCost, 1000)

    watchEffect(async () => {
      // unfortunately this runs as soon as the main screen is loaded,
      // not when the dialog is opened, so it's always watching. But
      // it's only watching these vars so it shouldn't make more than
      // a few API calls. But it's not great that it runs once every
      // page load.
      const nFrames = (state.frameEnd as number) - (state.frameStart as number) + 1
      const resolution = renderResolutionsByName[state.renderResolution as RenderResolution]
      nextTick(() => {        // use nextTick to avoid dependency loop
        state.costLoading = true
      })
      setCostDebounced(nFrames, resolution?.width || 1920, resolution?.height || 1080,
                       'video', state.renderQuality)
    })

    const previewTooLarge = computed(() => {
      const resolution = renderResolutionsByName[state.renderResolution as RenderResolution]
      if (!resolution)
        return false
      return state.renderQuality == 'preview' &&
        (resolution.width * resolution.height > 1900 * 1000) // no HD or higher previews
    })

    const renderButtonText = computed(() => {
      if (previewTooLarge.value)
        return 'Preview res too high'
      return 'Render'
    })

    watchEffect(() => {
      if (state && account.value)
        state.renderDisabled = extraCreditsNeeded.value > 0 || previewTooLarge.value
      else
        state.renderDisabled = true
    })

    const vbp: ComputedRef<Breakpoint> = computed(() => context.root.$vuetify.breakpoint)
    const cardTitleClasses = computed(() => ({
      'subtitle-1': vbp.value.smAndDown,
      'px-5': vbp.value.smAndDown,
      'py-2': vbp.value.smAndDown,
    }))

    watch(
      () => props.frame,
      (frame: number, prevFrame: number) => {
        if (frame !== prevFrame && props.mode === 'still') {
          state.frameStart = frame
          state.frameEnd = frame
        }
      }
    )

    function doTrackBeginCheckout() {
      trackBeginCheckout({amount: extraCreditsNeeded.value,
                          'n_frames': state.frameEnd - state.frameStart + 1,
                          'quantity': 1,
                          mode: props.mode,
                          'render_quality': state.renderQuality,
                          'render_resolution': state.renderResolution,
                          'video_cost': state.cost,
                          affiliation: state.renderQuality == 'preview' ? 'preview' : 'render'
      })
    }

    return {state, close, setFrameStart, setFrameEnd, render, vbp, cardTitleClasses, accountCredits,
            extraCreditsNeeded, renderButtonText, trackBeginCheckout, doTrackBeginCheckout}
  }
})
